import { useState } from 'react';
import {
  Building2, Users, GraduationCap, ExternalLink,
  ChevronDown, UserCheck, Clock, BookOpen, LogIn, MapPin,
  Globe, CheckCircle2, Mail, Copy, RotateCcw, AlertTriangle, X, Eye, EyeOff,
  Trash2, Calendar,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useImpersonate } from '../../../hooks/useImpersonate';
import { api } from '../../../api/client';
import { EmptyStateCard } from './EmptyStateCard';
import { normalizeActivationLink } from '../../../components/invite';

interface EscolasPanelProps {
  ultimasEscolas: any[];
  expandedId: string | null;
  setExpandedId: (id: string) => void;
  totalMunicipios: number;
}

export function EscolasPanel({ ultimasEscolas, expandedId, setExpandedId, totalMunicipios }: EscolasPanelProps) {
  const nav = useNavigate();
  const { impersonate, impersonating } = useImpersonate();

  // ─── Reset modal state ───
  const [resetModal, setResetModal] = useState<{
    open: boolean;
    escola: any;
    senha: string;
    loading: boolean;
    erro: string;
    sucesso: { linkAtivacao: string; diretorNome: string; diretorEmail: string } | null;
  }>({ open: false, escola: null, senha: '', loading: false, erro: '', sucesso: null });

  const [showSenha, setShowSenha] = useState(false);

  // ─── Delete modal state ───
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    escola: any;
    senha: string;
    confirmName: string;
    loading: boolean;
    erro: string;
    sucesso: { escolaNome: string; registrosRemovidos: number } | null;
  }>({ open: false, escola: null, senha: '', confirmName: '', loading: false, erro: '', sucesso: null });

  const [showSenhaDelete, setShowSenhaDelete] = useState(false);

  async function handleDelete() {
    if (!deleteModal.escola || !deleteModal.senha) return;
    if (deleteModal.confirmName !== deleteModal.escola.nome) {
      setDeleteModal(s => ({ ...s, erro: 'O nome da escola não confere. Digite exatamente como aparece acima.' }));
      return;
    }
    setDeleteModal(s => ({ ...s, loading: true, erro: '' }));
    try {
      const { data } = await api.post('/master/dashboard/apagar-escola', {
        escolaId: deleteModal.escola.id,
        senha: deleteModal.senha,
      });
      setDeleteModal(s => ({
        ...s, loading: false, erro: '', senha: '', confirmName: '',
        sucesso: { escolaNome: data.escolaNome, registrosRemovidos: data.registrosRemovidos },
      }));
    } catch (err: any) {
      console.warn('[EscolasPanel] Erro ao excluir escola:', err);
      const msg = err?.response?.data?.message || 'Falha na comunicação com o servidor.';
      setDeleteModal(s => ({ ...s, loading: false, erro: msg }));
    }
  }

  function fecharDeleteModal() {
    if (deleteModal.sucesso) window.location.reload();
    setDeleteModal({ open: false, escola: null, senha: '', confirmName: '', loading: false, erro: '', sucesso: null });
  }

  async function handleReset() {
    if (!resetModal.escola || !resetModal.senha) return;
    setResetModal(s => ({ ...s, loading: true, erro: '' }));
    try {
      const { data } = await api.post('/master/dashboard/reset-onboarding', {
        escolaId: resetModal.escola.id,
        senha: resetModal.senha,
      });
      setResetModal(s => ({
        ...s, loading: false, erro: '', senha: '',
        sucesso: { linkAtivacao: data.linkAtivacao, diretorNome: data.diretorNome, diretorEmail: data.diretorEmail },
      }));
    } catch (err: any) {
      console.warn('[EscolasPanel] Erro ao resetar onboarding:', err);
      const msg = err?.response?.data?.message || 'Falha na comunicação com o servidor.';
      setResetModal(s => ({ ...s, loading: false, erro: msg }));
    }
  }

  function fecharModal() {
    if (resetModal.sucesso) window.location.reload();
    setResetModal({ open: false, escola: null, senha: '', loading: false, erro: '', sucesso: null });
  }

  if (!ultimasEscolas || ultimasEscolas.length === 0) {
    return (
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border-color)' }}>
        <EmptyStateCard
          icon={totalMunicipios > 0 ? Building2 : MapPin}
          title={totalMunicipios > 0 ? 'Nenhuma escola cadastrada ainda' : 'Cadastre um município primeiro'}
          description={totalMunicipios > 0
            ? 'Cadastre a primeira escola para começar a acompanhar o progresso de alunos, professores e turmas em tempo real.'
            : 'É preciso ter pelo menos um município cadastrado antes de criar a primeira escola.'
          }
          action={{ label: totalMunicipios > 0 ? '+ Cadastrar Escola' : '+ Cadastrar Município', onClick: () => totalMunicipios > 0 ? nav('/master/escolas', { state: { openNew: true } }) : nav('/master/municipios', { state: { openNew: true } }) }}
        />
      </div>
    );
  }

  return (
    <div className="master-escolas-panel" style={{ marginBottom: 0 }} data-tour="dash.master.escolas">

      {/* ─── Modal de Apagar Escola ─── */}
      {deleteModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={e => { if (e.target === e.currentTarget) fecharDeleteModal(); }}>
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 20,
            padding: '28px 32px', width: '100%', maxWidth: 480,
            boxShadow: '0 24px 80px rgba(0,0,0,.18)', border: '1px solid var(--border-color)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Trash2 size={18} style={{ color: '#dc2626' }} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '.95rem', color: '#dc2626' }}>Apagar Escola Permanentemente</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>{deleteModal.escola?.nome}</div>
                </div>
              </div>
              <button onClick={fecharDeleteModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 8 }}>
                <X size={20} />
              </button>
            </div>

            {!deleteModal.sucesso ? (
              <>
                {/* Aviso GRAVE */}
                <div style={{ background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: '.78rem', color: '#dc2626', lineHeight: 1.5 }}>
                      <strong>PERIGO — Operação IRREVERSÍVEL!</strong> Esta operação irá <strong>APAGAR PERMANENTEMENTE</strong> a escola e <strong>TODOS</strong> os seus dados do banco de dados: turmas, alunos, professores, matérias, chamadas, comunicados, usuários e tudo mais. <strong>Não há como desfazer.</strong>
                    </div>
                  </div>
                </div>

                {/* Confirmar nome da escola */}
                <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Digite o nome da escola para confirmar: <strong style={{ color: '#dc2626' }}>{deleteModal.escola?.nome}</strong>
                </label>
                <input
                  type="text"
                  placeholder="Digite o nome da escola..."
                  value={deleteModal.confirmName}
                  onChange={e => setDeleteModal(s => ({ ...s, confirmName: e.target.value }))}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10, marginBottom: 14,
                    border: '2px solid #dc2626', background: 'var(--bg-main)',
                    color: 'var(--text-main)', fontSize: '.9rem',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />

                {/* Campo de senha */}
                <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Confirme sua senha de administrador
                </label>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <input
                    type={showSenhaDelete ? 'text' : 'password'}
                    placeholder="Digite sua senha..."
                    value={deleteModal.senha}
                    onChange={e => setDeleteModal(s => ({ ...s, senha: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleDelete(); }}
                    style={{
                      width: '100%', padding: '10px 44px 10px 14px', borderRadius: 10,
                      border: '2px solid #dc2626', background: 'var(--bg-main)',
                      color: 'var(--text-main)', fontSize: '.9rem',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenhaDelete(v => !v)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#dc2626', display: 'flex', alignItems: 'center', padding: 4,
                    }}
                  >
                    {showSenhaDelete ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Erro */}
                {deleteModal.erro && (
                  <div style={{ fontSize: '.78rem', color: '#dc2626', marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <AlertTriangle size={13} /> {deleteModal.erro}
                  </div>
                )}

                {/* Bot\u00f5es */}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={fecharDeleteModal} style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--border-color)',
                    background: 'transparent', color: 'var(--text-muted)', fontWeight: 600,
                    fontSize: '.85rem', cursor: 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteModal.loading || !deleteModal.senha || deleteModal.confirmName !== deleteModal.escola?.nome}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                      background: (deleteModal.loading || !deleteModal.senha || deleteModal.confirmName !== deleteModal.escola?.nome) ? '#e2e8f0' : '#dc2626',
                      color: (deleteModal.loading || !deleteModal.senha || deleteModal.confirmName !== deleteModal.escola?.nome) ? '#94a3b8' : 'white',
                      fontWeight: 700, fontSize: '.85rem',
                      cursor: (deleteModal.loading || !deleteModal.senha || deleteModal.confirmName !== deleteModal.escola?.nome) ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    {deleteModal.loading
                      ? <><span style={{ width: 14, height: 14, border: '2px solid #fff4', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }} /> Apagando...</>
                      : <><Trash2 size={14} /> Apagar Permanentemente</>
                    }
                  </button>
                </div>
              </>
            ) : (
              /* Tela de sucesso */
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <CheckCircle2 size={26} style={{ color: '#10b981' }} />
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-main)', marginBottom: 4 }}>Escola apagada com sucesso!</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                    <strong>{deleteModal.sucesso.escolaNome}</strong> e todos os seus dados foram removidos permanentemente.
                  </div>
                </div>
                <div style={{ background: 'var(--bg-main)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: '.8rem', color: 'var(--text-main)', fontWeight: 600 }}>
                    {deleteModal.sucesso.registrosRemovidos} registros removidos
                  </div>
                </div>
                <button onClick={fecharDeleteModal} style={{
                  width: '100%', padding: '10px 0', borderRadius: 10, border: 'none',
                  background: '#6366f1', color: 'white', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer',
                }}>Fechar e Atualizar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Modal de Reset Onboarding ─── */}
      {resetModal.open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={e => { if (e.target === e.currentTarget) fecharModal(); }}>
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 20,
            padding: '28px 32px', width: '100%', maxWidth: 480,
            boxShadow: '0 24px 80px rgba(0,0,0,.18)', border: '1px solid var(--border-color)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RotateCcw size={18} style={{ color: '#d97706' }} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '.95rem', color: 'var(--text-main)' }}>Resetar Onboarding</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>{resetModal.escola?.nome}</div>
                </div>
              </div>
              <button onClick={fecharModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 8 }}>
                <X size={20} />
              </button>
            </div>

            {!resetModal.sucesso ? (
              <>
                {/* Aviso */}
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: '.78rem', color: '#dc2626', lineHeight: 1.5 }}>
                      <strong>Atenção:</strong> Esta operação irá <strong>apagar todos os dados</strong> da escola — turmas, alunos, matérias, turnos, professores, comunicados e mais. O diretor será reiniciado como <strong>convidado</strong> com um novo link de ativação.
                    </div>
                  </div>
                </div>

                {/* Campo de senha */}
                <label style={{ display: 'block', fontSize: '.78rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  Confirme sua senha de administrador
                </label>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <input
                    type={showSenha ? 'text' : 'password'}
                    autoFocus
                    placeholder="Digite sua senha..."
                    value={resetModal.senha}
                    onChange={e => setResetModal(s => ({ ...s, senha: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') handleReset(); }}
                    style={{
                      width: '100%', padding: '10px 44px 10px 14px', borderRadius: 10,
                      border: '2px solid #6366f1', background: 'var(--bg-main)',
                      color: 'var(--text-main)', fontSize: '.9rem',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenha(v => !v)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#6366f1', display: 'flex', alignItems: 'center', padding: 4,
                    }}
                  >
                    {showSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Dica: senha padrão */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Senha padrão:</span>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--bg-main)', border: '1px solid var(--border-color)',
                    borderRadius: 8, padding: '4px 10px',
                  }}>
                    <code style={{ fontSize: '.8rem', fontWeight: 700, color: '#6366f1', letterSpacing: '.03em' }}>Admin@2026</code>
                    <button
                      type="button"
                      title="Copiar e preencher campo"
                      onClick={() => setResetModal(s => ({ ...s, senha: 'Admin@2026' }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', display: 'flex', alignItems: 'center', padding: 0 }}
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </div>

                {/* Erro */}
                {resetModal.erro && (
                  <div style={{ fontSize: '.78rem', color: '#dc2626', marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <AlertTriangle size={13} /> {resetModal.erro}
                  </div>
                )}

                {/* Botões */}
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button onClick={fecharModal} style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--border-color)',
                    background: 'transparent', color: 'var(--text-muted)', fontWeight: 600,
                    fontSize: '.85rem', cursor: 'pointer',
                  }}>Cancelar</button>
                  <button
                    onClick={handleReset}
                    disabled={resetModal.loading || !resetModal.senha}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                      background: resetModal.loading || !resetModal.senha ? '#e2e8f0' : '#dc2626',
                      color: resetModal.loading || !resetModal.senha ? '#94a3b8' : 'white',
                      fontWeight: 700, fontSize: '.85rem', cursor: resetModal.loading || !resetModal.senha ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    {resetModal.loading
                      ? <><span style={{ width: 14, height: 14, border: '2px solid #fff4', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }} /> Processando...</>
                      : <><RotateCcw size={14} /> Confirmar Reset</>
                    }
                  </button>
                </div>
              </>
            ) : (
              /* Tela de sucesso */
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <CheckCircle2 size={26} style={{ color: '#10b981' }} />
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-main)', marginBottom: 4 }}>Reset realizado com sucesso!</div>
                  <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Todos os dados da escola foram desativados.</div>
                </div>
                <div style={{ background: 'var(--bg-main)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Novo link de ativação do diretor</div>
                  <div style={{ fontSize: '.72rem', color: '#6366f1', marginBottom: 6 }}>
                    {resetModal.sucesso.diretorNome && <strong>{resetModal.sucesso.diretorNome}</strong>}
                    {' '}<span style={{ color: 'var(--text-muted)' }}>{resetModal.sucesso.diretorEmail}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      readOnly value={normalizeActivationLink(resetModal.sucesso.linkAtivacao)}
                      style={{ flex: 1, fontSize: '.7rem', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-main)', outline: 'none' }}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(normalizeActivationLink(resetModal.sucesso!.linkAtivacao))}
                      style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #6366f1', background: '#6366f115', color: '#6366f1', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <Copy size={11} /> Copiar
                    </button>
                  </div>
                </div>
                <button onClick={fecharModal} style={{
                  width: '100%', padding: '10px 0', borderRadius: 10, border: 'none',
                  background: '#6366f1', color: 'white', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer',
                }}>Fechar e Atualizar</button>
              </div>
            )}
          </div>
        </div>
      )}

      <h3 style={{ fontSize: '.95rem', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Building2 size={18} style={{ color: '#10b981' }} />
        Últimas Escolas Cadastradas
        <span style={{ fontSize: '.72rem', fontWeight: 500, color: 'var(--text-muted, #9ca3af)', marginLeft: 'auto' }}>
          {ultimasEscolas.length} mais recentes
        </span>
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ultimasEscolas.map((esc: any, idx: number) => {
          const sd = esc.setupData || {};
          const isExpanded = expandedId === esc.id;
          const isWizardDone = esc.wizardConcluido === true;
          const isEquipeOk = (sd.equipe || 0) > 0 || isWizardDone || Boolean(esc.nomeDiretor || esc.diretorEscola);
          const isGradeOk = (sd.grade || 0) > 0 || isWizardDone || (Number(esc.wizardEtapaAtual || 0) >= 7);

          const steps = [
            isEquipeOk,
            (sd.turnos || 0) > 0 || isWizardDone,
            (sd.materias || 0) > 0 || isWizardDone,
            (sd.turmas || 0) > 0 || isWizardDone,
            (sd.professores || 0) > 0 || isWizardDone,
            (sd.alunos || 0) > 0 || isWizardDone,
            isGradeOk,
          ];
          const done = isWizardDone ? 7 : steps.filter(Boolean).length;
          const total = 7;
          const pct = isWizardDone ? 100 : Math.round((done / total) * 100);

          let ringColor = '#94a3b8';
          if (pct > 25 && pct <= 50) ringColor = '#3b82f6';
          else if (pct > 50 && pct < 100) ringColor = '#f59e0b';
          else if (pct === 100) ringColor = '#10b981';

          const tipoColors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b' };
          const tc = tipoColors[esc.tipoRede] || '#6b7280';
          const dir = esc.diretorEscola ?? esc.ultimosAcessos?.find((u: any) => u.cargo === 'DIRETOR' || u.perfil === 'ESCOLA');

          return (
            <div
              key={esc.id}
              className="master-escola-card"
              data-tour={`page.card.${idx + 1}.escola`}
              style={{
              background: 'var(--bg-card, white)', borderRadius: 16,
              border: '1px solid var(--border-color, #e5e7eb)', padding: '14px',
              transition: 'box-shadow .15s', display: 'flex', flexDirection: 'column', gap: 10,
            }} onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.06)'; }}
               onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}>

              {/* Header */}
              <div onClick={() => setExpandedId(isExpanded ? null : esc.id)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Circular Progress */}
                  <div style={{ position: 'relative', width: 38, height: 38, flexShrink: 0 }}>
                    <svg width="38" height="38" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="var(--border-color, #e2e8f0)" strokeWidth="4" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={ringColor} strokeWidth="4" strokeDasharray={`${pct}, 100`} strokeLinecap="round" />
                    </svg>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.66rem', fontWeight: 800, color: ringColor }}>
                      {pct}%
                    </div>
                  </div>
                  {/* Info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-main, #111827)' }}>{esc.nome}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.65rem', fontWeight: 600, background: `${tc}18`, color: tc, flexShrink: 0 }}>{esc.tipoRede}</span>
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--text-muted, #6b7280)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{esc.codigo}</span>
                      <span>&bull;</span>
                      <span>{esc.municipio?.nome || esc.cidade} / {esc.municipio?.estado?.sigla || esc.uf}</span>
                      {esc.nomeDiretor && <><span>&bull;</span><span>Dir. {esc.nomeDiretor}</span></>}
                      <span>&bull;</span>
                      <span>{esc._count?.turmas || 0} turmas</span>
                      <span>&bull;</span>
                      <span>{esc._count?.alunos || 0} alunos</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  {/* Botão Apagar Escola — sempre visível */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteModal({ open: true, escola: esc, senha: '', confirmName: '', loading: false, erro: '', sucesso: null });
                    }}
                    title="Apagar escola permanentemente do banco de dados"
                    aria-label="Apagar escola"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 9,
                      background: 'transparent', border: '1px solid #dc2626',
                      cursor: 'pointer', color: '#dc2626',
                      transition: 'all .15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Trash2 size={14} />
                  </button>
                  {/* Botão Resetar — só aparece se NÃO concluiu todas as etapas */}
                  {pct < 100 && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setResetModal({ open: true, escola: esc, senha: '', loading: false, erro: '', sucesso: null });
                      }}
                      title="Resetar onboarding desta escola"
                      aria-label="Resetar onboarding da escola"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 9,
                        background: 'transparent', border: '1px solid #f59e0b',
                        cursor: 'pointer', color: '#d97706',
                        transition: 'all .15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); nav(`/master/escolas/${esc.id}`); }} title="Ver detalhes da escola" aria-label="Ver detalhes da escola" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 9,
                    background: 'white', border: '1px solid var(--border-color, #e5e7eb)',
                    cursor: 'pointer', color: 'var(--text-main)',
                    boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                  }}><ExternalLink size={14} /></button>
                  <button onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : esc.id); }} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%',
                    background: 'var(--bg-main, #f8fafc)', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                    transition: 'all .2s'
                  }}>
                    <ChevronDown size={18} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .3s' }} />
                  </button>
                </div>
              </div>

              {/* Detalhes Colapsáveis */}
              {isExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Trilha de Configuração */}
                  <div style={{ background: 'var(--bg-main, #f8fafc)', borderRadius: 12, padding: '16px 20px', border: '1px solid var(--border-color, #f1f5f9)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-main, #334155)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Trilha de Configuração</span>
                      <span style={{ fontSize: '.7rem', fontWeight: 700, borderRadius: 12, padding: '4px 10px', background: pct === 100 ? '#dcfce7' : '#dbeafe', color: pct === 100 ? '#16a34a' : '#2563eb' }}>
                        {done}/{total} ({pct}%)
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
                      {[
                        { key: 'equipe', label: 'Equipe', ok: isEquipeOk, icon: Users },
                        { key: 'turnos', label: 'Turnos', ok: (sd.turnos || 0) > 0 || isWizardDone, icon: Clock },
                        { key: 'materias', label: 'Matérias', ok: (sd.materias || 0) > 0 || isWizardDone, icon: BookOpen },
                        { key: 'turmas', label: 'Turmas', ok: (sd.turmas || 0) > 0 || isWizardDone, icon: GraduationCap },
                        { key: 'professores', label: 'Professores', ok: (sd.professores || 0) > 0 || isWizardDone, icon: UserCheck },
                        { key: 'alunos', label: 'Alunos', ok: (sd.alunos || 0) > 0 || isWizardDone, icon: Users },
                        { key: 'grade', label: 'Grade', ok: isGradeOk, icon: Calendar },
                      ].map((step, stepIdx, arr) => {
                        const StepIcon = step.ok ? CheckCircle2 : step.icon;
                        return (
                          <div key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', minWidth: 0 }}>
                            {stepIdx < arr.length - 1 && (
                              <div style={{ position: 'absolute', top: 12, left: '50%', width: '100%', height: 2, background: step.ok ? '#16a34a' : '#cbd5e1', zIndex: 0 }} />
                            )}
                            <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, background: step.ok ? '#16a34a' : 'white', border: `2px solid ${step.ok ? '#16a34a' : '#cbd5e1'}`, color: step.ok ? 'white' : '#94a3b8', transition: 'all .2s' }}>
                              <StepIcon size={14} />
                            </div>
                            <span style={{ marginTop: 6, fontSize: '.6rem', fontWeight: 600, color: step.ok ? '#16a34a' : '#64748b', textAlign: 'center', lineHeight: 1.2 }}>{step.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Acessos e Gestores */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 }}>
                    {/* Últimos Acessos */}
                    <div style={{ background: 'var(--bg-main, #f8fafc)', borderRadius: 12, padding: '16px', border: '1px solid var(--border-color, #f1f5f9)' }}>
                      <span style={{ display: 'block', fontSize: '.65rem', fontWeight: 700, color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>Últimos acessos</span>
                      {(esc.ultimosAcessos || []).length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(esc.ultimosAcessos || []).map((u: any) => (
                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '.8rem', background: 'white', padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: u.ativo ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
                                <span style={{ fontWeight: 600, color: 'var(--text-main, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <span style={{ color: '#64748b', fontWeight: 500 }}>{u.cargo === 'DIRETOR' ? 'Dir.' : u.cargo === 'COORDENACAO' ? 'Coord.' : u.cargo === 'SECRETARIA' ? 'Sec.' : u.perfil === 'PROFESSOR' ? 'Prof.' : ''}</span> {u.nome?.split(' ').slice(0, 2).join(' ')}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                <span style={{ color: '#64748b', fontSize: '.7rem', fontFamily: 'monospace' }}>
                                  {u.ultimoLogin ? new Date(u.ultimoLogin).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + new Date(u.ultimoLogin).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '\u2014'}
                                </span>
                                {u.ativo && (
                                  <button onClick={() => impersonate(u.id)} disabled={impersonating === u.id} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, background: '#6366f115', color: '#6366f1', border: 'none', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer', transition: 'all .2s' }}><LogIn size={10} /> Entrar</button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Nenhum acesso registrado.</div>
                      )}
                    </div>

                    {/* Acompanhamento */}
                    <div style={{ background: 'var(--bg-main, #f8fafc)', borderRadius: 12, padding: '16px', border: '1px solid var(--border-color, #f1f5f9)' }}>
                      <span style={{ display: 'block', fontSize: '.65rem', fontWeight: 700, color: 'var(--text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 10 }}>Acompanhamento</span>
                      {!(dir || esc.gestorMunicipal || esc.gestorEstadual) ? (
                        <div style={{ fontSize: '.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Nenhum acompanhamento vinculado.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {/* Diretor — aparece mesmo PENDENTE */}
                          {dir && (() => {
                            const isPendente = dir.statusConta === 'PENDENTE' || dir.statusConta === 'PENDENTE_ATIVACAO';
                            const statusColor = isPendente ? '#d97706' : '#059669';
                            const statusBg = isPendente ? '#fef3c7' : '#d1fae5';
                            const activationUrl = dir.tokenAtivacao
                              ? `${window.location.protocol}//${window.location.host}/ativar?token=${dir.tokenAtivacao}${dir.codigoDisplay ? `&code=${dir.codigoDisplay}` : ''}`
                              : null;
                            return (
                              <div style={{ background: 'white', padding: '8px 10px', borderRadius: 8, border: `1px solid ${statusColor}30` }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.6rem', fontWeight: 700, background: statusBg, color: statusColor, flexShrink: 0 }}>
                                      {isPendente ? '⏳ PENDENTE' : '✓ DIRETOR'}
                                    </span>
                                    <UserCheck size={13} style={{ color: '#475569', flexShrink: 0 }} />
                                    <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-main, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {dir.nome?.split(' ').slice(0, 2).join(' ') || dir.email || 'Diretor'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    {isPendente && activationUrl && (
                                      <button
                                        title="Copiar link de ativação"
                                        onClick={() => { navigator.clipboard.writeText(activationUrl); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, background: '#f59e0b15', color: '#d97706', border: 'none', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}
                                      >
                                        <Copy size={10} /> Copiar link
                                      </button>
                                    )}
                                    {!isPendente && (
                                      <button onClick={() => impersonate(dir.id)} disabled={impersonating === dir.id}
                                        style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, background: '#6366f115', color: '#6366f1', border: 'none', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}>
                                        <LogIn size={10} /> Entrar
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {/* Email / último login */}
                                <div style={{ marginTop: 6, fontSize: '.7rem', color: '#94a3b8', display: 'flex', gap: 12, alignItems: 'center' }}>
                                  {dir.email && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={10} />{dir.email}</span>}
                                  {dir.ultimoLogin
                                    ? <span>Último acesso: {new Date(dir.ultimoLogin).toLocaleDateString('pt-BR')}</span>
                                    : <span style={{ color: '#d97706' }}>Nunca acessou</span>
                                  }
                                </div>
                              </div>
                            );
                          })()}
                          {esc.gestorMunicipal && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'white', padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.6rem', fontWeight: 700, background: esc.gestorMunicipal.statusConta === 'ATIVA' ? '#10b98115' : '#f59e0b15', color: esc.gestorMunicipal.statusConta === 'ATIVA' ? '#059669' : '#d97706' }}>MUNICIPAL</span>
                                <MapPin size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-main, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{esc.gestorMunicipal.nome?.split(' ').slice(0, 2).join(' ')}</span>
                              </div>
                              {esc.gestorMunicipal.ativo && esc.gestorMunicipal.statusConta === 'ATIVA' && (
                                <button onClick={() => impersonate(esc.gestorMunicipal.id)} disabled={impersonating === esc.gestorMunicipal.id} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, background: '#6366f115', color: '#6366f1', border: 'none', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}><LogIn size={10} /> Entrar</button>
                              )}
                            </div>
                          )}
                          {esc.gestorEstadual && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: 'white', padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.6rem', fontWeight: 700, background: '#6366f115', color: '#6366f1' }}>ESTADUAL</span>
                                <Globe size={13} style={{ color: '#2563eb', flexShrink: 0 }} />
                                <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-main, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{esc.gestorEstadual.nome?.split(' ').slice(0, 2).join(' ')}</span>
                              </div>
                              {esc.gestorEstadual.ativo && esc.gestorEstadual.statusConta === 'ATIVA' && (
                                <button onClick={() => impersonate(esc.gestorEstadual.id)} disabled={impersonating === esc.gestorEstadual.id} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, background: '#6366f115', color: '#6366f1', border: 'none', fontSize: '.65rem', fontWeight: 700, cursor: 'pointer' }}><LogIn size={10} /> Entrar</button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
